import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { CircularProgress } from "@mui/material";

import IPLMatchCardCompleted from "../components/home/IPLMatchCardCompleted";
import IPLMatchCardLive from "../components/home/IPLMatchCardLive";
import IPLMatchCardUpcoming from "../components/home/IPLMatchCardUpcoming";

import BundesligaCard from "../components/tournament/BundesligaCard";
import MatchTabs from "../components/tournament/MatchTabs";
import ParticipatingTeams from "../components/tournament/ParticipatingTeams";
import PointsTable from "../components/tournament/PointsTable";
import VenuesCard from "../components/tournament/VenuesCard";
import ViewPointsTableButton from "../components/tournament/ViewPointsTableButton";

import apiClient from "../utils/apiClient";

const TournamentDetails = () => {

  const { slug } = useParams();

  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [standings, setStandings] = useState([]);

  const [loadingEvents, setLoadingEvents] =
    useState(true);

  const [activeFilter, setActiveFilter] =
    useState("all");

  // =========================
  // GET EVENTS
  // =========================

  const getEvents = async () => {
    try {
      setLoadingEvents(true);

      const response = await apiClient.get(
        "/public/tournament/" + slug
      );

      const result = await response?.data;

      if (result?.success) {

        setEvents(
          result.data?.allEvents?.filter(
            (res) => res.match_id
          )
        );

        setTeams(
          result.data?.allTeams?.filter(
            (res) => res.team_key
          )
        );

        setStandings(
          result.data?.allStandings?.filter(
            (res) => res.team_id
          )
        );
      }

    } catch (error) {
      console.log(
        "Tournament API Error:",
        error
      );
    } finally {
      setLoadingEvents(false);
    }
  };

  useEffect(() => {
    if (slug) {
      getEvents();
    }
  }, [slug]);

  // =========================
  // MATCH TYPES
  // =========================

  const liveMatches = events?.filter(
    (match) =>
      match?.match_live === "1" ||
      match?.match_status === "Live"
  );

  const upcomingMatches = events?.filter(
    (match) => {

      const isLive =
        match?.match_live === "1" ||
        match?.match_status === "Live";

      return (
        !isLive &&
        (
          !match?.match_status ||
          match?.match_status === "Upcoming"
        )
      );
    }
  );

  const completedMatches = events?.filter(
    (match) => {

      const isLive =
        match?.match_live === "1" ||
        match?.match_status === "Live";

      const isUpcoming =
        !match?.match_status ||
        match?.match_status === "Upcoming";

      return !isLive && !isUpcoming;
    }
  );

  // =========================
  // FILTERED MATCHES
  // =========================

  const filteredMatches = events?.filter(
    (match) => {

      // LIVE
      const isLive =
        match?.match_live === "1" ||
        match?.match_status === "Live";

      // UPCOMING
      const isUpcoming =
        !isLive &&
        (
          !match?.match_status ||
          match?.match_status === "Upcoming"
        );

      // COMPLETED
      const isCompleted =
        !isLive && !isUpcoming;

      // FILTERS
      if (activeFilter === "live") {
        return isLive;
      }

      if (activeFilter === "upcoming") {
        return isUpcoming;
      }

      if (activeFilter === "completed") {
        return isCompleted;
      }

      return true;
    }
  );

  // =========================
  // HEADER DATA
  // =========================

  const data = {
    league_name:
      standings[0]?.league_name,

    league_logo:
      events[0]?.league_logo,

    league_year:
      events[0]?.league_year,

    totalMatches:
      events?.length,

    totalTeams:
      teams?.length,

    totalVenues:
      teams?.length,
  };

  // =========================
  // LOADER
  // =========================

  if (loadingEvents) {
    return (
      <div className="flex justify-center py-6">
        <CircularProgress size={24} />
      </div>
    );
  }

  // =========================
  // SCROLL
  // =========================

  const handleScroll = () => {

    const section =
      document.getElementById(
        "points-table"
      );

    if (section) {
      section.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <>
      <div className="p-4">

        {/* TOP CARD */}
        <BundesligaCard data={data} />

        {/* TEAMS */}
        <ParticipatingTeams teams={teams} />

  {/* POINT TABLE */}
        <div id="points-table">
          <PointsTable
            standings={standings}
          />
        </div>

        {/* VENUES */}
        <VenuesCard teams={teams} />

        {/* BUTTON */}
        {/* <ViewPointsTableButton
          onClick={handleScroll}
        /> */}

        {/* FILTERS */}
        <MatchTabs
          activeFilter={activeFilter}
          setActiveFilter={setActiveFilter}
          counts={{
            all: events?.length || 0,
            live:
              liveMatches?.length || 0,
            completed:
              completedMatches?.length || 0,
            upcoming:
              upcomingMatches?.length || 0,
          }}
        />

        {/* MATCHES */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 mb-8">

          {filteredMatches?.map((match) => {

            // LIVE
            const isLive =
              match?.match_live === "1" ||
              match?.match_status === "Live";

            // UPCOMING
            const isUpcoming =
              !isLive &&
              (
                !match?.match_status ||
                match?.match_status ===
                  "Upcoming"
              );

            // COMPLETED
            const isCompleted =
              !isLive && !isUpcoming;

            // LIVE CARD
            if (isLive) {
              return (
                <IPLMatchCardLive
                  key={match?.match_id}
                  match={match}
                />
              );
            }

            // UPCOMING CARD
            if (isUpcoming) {
              return (
                <IPLMatchCardUpcoming
                  key={match?.match_id}
                  match={match}
                />
              );
            }

            // COMPLETED CARD
            if (isCompleted) {
              return (
                <IPLMatchCardCompleted
                  key={match?.match_id}
                  match={match}
                />
              );
            }

            return null;
          })}

          {/* EMPTY */}
          {filteredMatches?.length === 0 && (
            <div className="col-span-full w-full flex items-center justify-center py-16">

              <div className="text-center">

                <div className="text-6xl mb-4">
                  ⚽
                </div>

                <h3 className="text-2xl font-bold text-gray-800 dark:text-white">
                  No Match Found
                </h3>

                <p className="text-gray-500 dark:text-gray-400 mt-2">
                  No matches available right now.
                </p>

              </div>

            </div>
          )}

        </div>

      

      </div>
    </>
  );
};

export default TournamentDetails;